import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, Observable, of, switchMap, tap } from 'rxjs';
import { NouveauteSection, NouveauteSectionFormData } from './nouveaute.model';
import { NouveautesService, NouveautePayload } from './nouveautes.service';
import { ToasterService } from '../../layout/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';

type SavePayload = Omit<NouveautePayload, 'sections'> & { sections: NouveauteSectionFormData[] };

@Injectable()
export class NouveautesStore {
  private readonly nouveautesService = inject(NouveautesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  private readonly saveTrigger = signal<{
    data: SavePayload;
    timestamp: number;
  } | null>(null);
  private readonly deleteTrigger = signal<{ id: string; timestamp: number } | null>(null);

  readonly nouveautes = computed(() => this.nouveautesResource.value() ?? []);
  readonly isLoading = computed(
    () =>
      this.nouveautesResource.isLoading() ||
      this.saveResource.isLoading() ||
      this.deleteResource.isLoading(),
  );
  readonly error = computed(() => this.nouveautesResource.error());

  private readonly nouveautesResource = rxResource({
    stream: () => this.nouveautesService.getAllNouveautes(),
  });

  readonly saveResource = rxResource({
    params: () => this.saveTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      const { data } = params;

      const sectionsWithFiles = data.sections
        .map((s, i) => ({ section: s, index: i }))
        .filter(({ section }) => !!section.imageFile);

      const upload$: Observable<string[]> =
        sectionsWithFiles.length > 0
          ? this.nouveautesService.uploadImages(
              sectionsWithFiles.map(({ section }) => section.imageFile!),
            )
          : of([]);

      return upload$.pipe(
        switchMap((keys) => {
          const keyByIndex = new Map<number, string>(
            sectionsWithFiles.map(({ index }, i) => [index, keys[i]]),
          );

          const cleanSections: NouveauteSection[] = data.sections.map((s, i) => ({
            sectionTitle: s.sectionTitle,
            markdownContent: s.markdownContent,
            imageKey: keyByIndex.get(i) ?? s.imageKey,
          }));

          const payload: NouveautePayload = { ...data, sections: cleanSections };
          return this.nouveautesService.saveNouveaute(payload);
        }),
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('GLOBAL.SAVE_SUCCESS'));
            this.nouveautesResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('GLOBAL.SAVE_ERROR'));
          });
          throw error;
        }),
      );
    },
  });

  readonly deleteResource = rxResource({
    params: () => this.deleteTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.nouveautesService.deleteNouveaute(params.id).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('NOUVEAUTES.DELETE_SUCCESS'));
            this.nouveautesResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('NOUVEAUTES.DELETE_ERROR'));
          });
          throw error;
        }),
      );
    },
  });

  saveNouveaute(data: SavePayload): void {
    this.saveTrigger.set({ data, timestamp: Date.now() });
  }

  deleteNouveaute(id: string): void {
    this.deleteTrigger.set({ id, timestamp: Date.now() });
  }
}

